
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { AxiosResponse } from 'axios';
import { mapState } from 'vuex';
import { getReturnLabelsPDF, getRunSheetList, getRunSheetPDFs, printReturnLabels } from '@/api';
import DateSelect from '@/components/dashBoard/selectsFields/components/DateSelect/DateSelect.vue';
import StateSelect from '@/components/dashBoard/selectsFields/components/StateSelect/StateSelect.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import DatePicker from '@/components/searchPage/DatePicker.vue';
import PrinterListDialog from '@/components/dialogs/PrinterListDialog.vue';
import { User } from '@/models/main';
import { RunSheetDriverDay } from '@/models/reports';

import { addDays } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { blobDownloader } from '@/utils/Common';
import { notificationErrorText, timeZoneDefault } from '@/utils/PortalConstants';

@Component({
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
  components: {
    Button,
    BackButton,
    DatePicker,
    DateSelect,
    PrinterListDialog,
    StateSelect,
  },
})
export default class RunSheet extends Vue {
  private isLoading = false;
  private isLabelsLoading = false;
  private isPrinterListShown = false;
  private user!: User;

  private dates: string[] = [];
  private states: string[] = [];

  private driversList = {};
  private checkedList = {};
  private checkedDateList = {};

  private getDriversList() {
    const params = {
      states: this.states,
      dates: this.dates,
    };

    this.isLoading = true;

    getRunSheetList(params)
      .then(res => {
        this.driversList = res;
        this.checkedList = {};
        Object.keys(this.driversList).map(item => {
          this.checkedDateList[item] = false;
        });
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error getting run sheet',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private changeDate(dates: string[]): void {
    this.dates = dates;
    this.getDriversList();
  }

  private changeStates(states: string[]) {
    this.states = states;
    this.getDriversList();
  }

  private checkboxClick(idx, id): void {
    if (this.driversList[idx][id].length >= 3) {
      if (!this.checkedList[idx]) {
        this.checkedList[idx] = [];
      }
      if (!this.checkedList[idx].includes(id)) {
        this.checkedList[idx].push(id);
      }
    } else {
      this.checkedDateList[idx] = false;
      this.checkedList[idx].splice(this.checkedList[idx].indexOf(id), 1);
      if (this.checkedList[idx].length === 0) {
        delete this.checkedList[idx];
      }
    }
    if (this.checkedList[idx] && this.checkedList[idx].length === this.driversList[idx].length) {
      this.checkedDateList[idx] = true;
    }
  }

  private checkboxDateClick(event, idx): void {
    this.checkedDateList[idx] = true;
    if (!this.checkedList[idx]) {
      this.checkedList[idx] = [];
      this.driversList[idx].map((item, i) => {
        if (item.indexOf('checked') === -1) {
          item.push('checked');
          this.checkedList[idx].push(i);
        }
      });
    } else if (this.checkedList[idx].length === this.driversList[idx].length) {
      this.driversList[idx].map(item => {
        item.splice(item.indexOf('checked'), 1);
      });
      delete this.checkedList[idx];
    } else {
      this.driversList[idx].map((item, i) => {
        if (item.indexOf('checked') === -1) {
          item.push('checked');
          this.checkedList[idx].push(i);
        }
      });
    }
  }

  // Methods rather than computeds: `checkedList` gains and loses keys without Vue.set,
  // so a cached computed would never see the change
  private isNothingChecked(): boolean {
    return Object.keys(this.checkedList).length === 0;
  }

  /** The `[date, driver]` pairs the user has ticked, the shape both report endpoints take */
  private checkedDrivers(): RunSheetDriverDay[] {
    const sendingList: RunSheetDriverDay[] = [];
    Object.keys(this.driversList).map((date: string) => {
      this.driversList[date].map((driver: [number]) => {
        if (driver && driver.length >= 3) {
          sendingList.push([date, driver[0]]);
        }
      });
    });
    return sendingList;
  }

  private downloadFile(): void {
    this.isLoading = true;

    getRunSheetPDFs(this.checkedDrivers())
      .then(res => {
        const blob = new Blob([res.data], { type: 'pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'runsheet.pdf';
        link.click();
        URL.revokeObjectURL(link.href);
        this.isLoading = false;
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Download error',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private downloadReturnLabels(): void {
    this.isLabelsLoading = true;

    getReturnLabelsPDF(this.checkedDrivers())
      .then(res => {
        if (res.status === 204) {
          this.$notify({
            group: 'network',
            title: 'No return labels',
            text: 'The selected drivers have no products to return.',
            type: 'warn',
          });
          return;
        }
        blobDownloader(res);
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Download error',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.isLabelsLoading = false;
      });
  }

  private printReturnLabelsRequest(printerId: number): Promise<AxiosResponse> {
    return printReturnLabels({ drivers: this.checkedDrivers(), printer_id: printerId });
  }

  private openPrinterList(): void {
    this.isPrinterListShown = true;
  }

  private closePrinterList(): void {
    this.isPrinterListShown = false;
  }

  private mounted(): void {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    const fromDate = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd'); // today
    const toDate = format(utcToZonedTime(addDays(new Date(), 5), userTimeZone), 'yyyy-MM-dd'); // +5 days
    this.dates = [fromDate, toDate];
  }
}
